import { useRef, useState, useEffect } from 'react'
import * as THREE from 'three'
import { RADIUS, heightAt, noise3, smoothstep, lerp } from './terrainGen'
import { useUIStore } from '@/state/useUIStore'
import { loadCachedTerrain, saveCachedTerrain } from './terrainCache'

export interface TerrainSphereProps {
  radius?: number
  maxLOD?: number
  seed?: number
  sunPosition?: THREE.Vector3
  onReady?: () => void
}

/** Yield to the browser so the loading UI can repaint */
function yieldToMain(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0))
}

export function TerrainSphere(props: TerrainSphereProps) {
  const meshRef = useRef<THREE.Mesh>(null)
  const onReadyRef = useRef(props.onReady)
  onReadyRef.current = props.onReady

  const [result, setResult] = useState<{
    geometry: THREE.BufferGeometry
    material: THREE.MeshStandardMaterial
  } | null>(null)

  useEffect(() => {
    let cancelled = false
    const progress = (pct: number, label: string) => {
      if (!cancelled) useUIStore.getState().setLoadProgress(pct, label)
    }

    async function generate() {
      // ── Try loading from IndexedDB cache ──────────────────────────────
      progress(15, 'Checking terrain cache...')
      await yieldToMain()
      if (cancelled) return

      const cached = await loadCachedTerrain()
      if (cached && !cancelled) {
        progress(40, 'Loading cached terrain...')
        await yieldToMain()
        if (cancelled) return

        const geo = new THREE.BufferGeometry()
        geo.setAttribute('position', new THREE.BufferAttribute(cached.positions, 3))
        geo.setAttribute('color', new THREE.BufferAttribute(cached.colors, 3))
        geo.setAttribute('normal', new THREE.BufferAttribute(cached.normals, 3))

        const mat = new THREE.MeshStandardMaterial({
          vertexColors: true,
          roughness: 1.0,
          metalness: 0.0,
          flatShading: false,
        })

        setResult({ geometry: geo, material: mat })
        onReadyRef.current?.()
        return
      }

      const BASE_DETAIL = 4
      const LAND_EXTRA = 7
      const HEIGHT_THRESHOLD = 0.0

      type Tri = [number, number, number]
      let verts: number[][] = []
      let tris: Tri[] = []

      // ── Phase 1: Build icosahedron + base subdivision ──────────────
      progress(15, 'Building base mesh...')
      await yieldToMain()
      if (cancelled) return

      const t = (1 + Math.sqrt(5)) / 2
      const addVert = (x: number, y: number, z: number) => {
        const len = Math.sqrt(x * x + y * y + z * z)
        verts.push([x / len, y / len, z / len])
        return verts.length - 1
      }
      addVert(-1, t, 0); addVert(1, t, 0); addVert(-1, -t, 0); addVert(1, -t, 0)
      addVert(0, -1, t); addVert(0, 1, t); addVert(0, -1, -t); addVert(0, 1, -t)
      addVert(t, 0, -1); addVert(t, 0, 1); addVert(-t, 0, -1); addVert(-t, 0, 1)
      tris = [
        [0,11,5],[0,5,1],[0,1,7],[0,7,10],[0,10,11],
        [1,5,9],[5,11,4],[11,10,2],[10,7,6],[7,1,8],
        [3,9,4],[3,4,2],[3,2,6],[3,6,8],[3,8,9],
        [4,9,5],[2,4,11],[6,2,10],[8,6,7],[9,8,1],
      ]

      const midpointCache = new Map<string, number>()
      const getMidpoint = (a: number, b: number) => {
        const key = Math.min(a, b) + ':' + Math.max(a, b)
        if (midpointCache.has(key)) return midpointCache.get(key)!
        const va = verts[a], vb = verts[b]
        const idx = addVert((va[0] + vb[0]) / 2, (va[1] + vb[1]) / 2, (va[2] + vb[2]) / 2)
        midpointCache.set(key, idx)
        return idx
      }

      for (let d = 0; d < BASE_DETAIL; d++) {
        const next: Tri[] = []
        midpointCache.clear()
        for (const [a, b, c] of tris) {
          const ab = getMidpoint(a, b)
          const bc = getMidpoint(b, c)
          const ca = getMidpoint(c, a)
          next.push([a, ab, ca], [b, bc, ab], [c, ca, bc], [ab, bc, ca])
        }
        tris = next
      }

      if (cancelled) return

      // ── Phase 2: Height computation ────────────────────────────────
      progress(17, 'Sampling terrain heights...')
      await yieldToMain()
      if (cancelled) return

      const vertHeight = verts.map(v => heightAt(v[0], v[1], v[2]))

      const ensureHeight = (idx: number) => {
        if (vertHeight.length <= idx) {
          vertHeight.push(heightAt(verts[idx][0], verts[idx][1], verts[idx][2]))
        }
      }
      const edgeKey = (a: number, b: number) => Math.min(a, b) + ':' + Math.max(a, b)

      if (cancelled) return

      // ── Phase 3–9: Adaptive land subdivision (7 iterations) ────────
      for (let d = 0; d < LAND_EXTRA; d++) {
        const pct = Math.round(19 + (d / LAND_EXTRA) * 18) // 19% → 37%
        progress(pct, `Refining land detail ${d + 1}/${LAND_EXTRA}...`)
        await yieldToMain()
        if (cancelled) return

        const next: Tri[] = []
        midpointCache.clear()

        const deferred: Tri[] = []
        for (const [a, b, c] of tris) {
          const ha = vertHeight[a], hb = vertHeight[b], hc = vertHeight[c]
          if (ha > HEIGHT_THRESHOLD || hb > HEIGHT_THRESHOLD || hc > HEIGHT_THRESHOLD) {
            const ab = getMidpoint(a, b)
            const bc = getMidpoint(b, c)
            const ca = getMidpoint(c, a)
            ensureHeight(ab); ensureHeight(bc); ensureHeight(ca)
            next.push([a, ab, ca], [b, bc, ab], [c, ca, bc], [ab, bc, ca])
          } else {
            deferred.push([a, b, c])
          }
        }

        for (const [a, b, c] of deferred) {
          const mAB = midpointCache.get(edgeKey(a, b))
          const mBC = midpointCache.get(edgeKey(b, c))
          const mCA = midpointCache.get(edgeKey(c, a))
          const splits = (mAB !== undefined ? 1 : 0) + (mBC !== undefined ? 1 : 0) + (mCA !== undefined ? 1 : 0)

          if (splits === 0) {
            next.push([a, b, c])
          } else if (splits === 3) {
            next.push([a, mAB!, mCA!], [b, mBC!, mAB!], [c, mCA!, mBC!], [mAB!, mBC!, mCA!])
          } else if (splits === 1) {
            if (mAB !== undefined) { next.push([a, mAB, c], [mAB, b, c]) }
            else if (mBC !== undefined) { next.push([a, b, mBC], [a, mBC, c]) }
            else { next.push([a, b, mCA!], [mCA!, b, c]) }
          } else {
            if (mAB !== undefined && mBC !== undefined) {
              next.push([a, mAB, c], [mAB, b, mBC], [mAB, mBC, c])
            } else if (mAB !== undefined && mCA !== undefined) {
              next.push([a, mAB, mCA!], [mAB, b, c], [mAB, c, mCA!])
            } else {
              next.push([a, b, mBC!], [a, mBC!, mCA!], [mCA!, mBC!, c])
            }
          }
        }

        tris = next
      }

      if (cancelled) return

      // ── Phase 10: Slope computation ────────────────────────────────
      progress(38, 'Computing terrain slopes...')
      await yieldToMain()
      if (cancelled) return

      const vertNeighbors: Set<number>[] = new Array(verts.length)
      for (let i = 0; i < verts.length; i++) vertNeighbors[i] = new Set()
      for (const [a, b, c] of tris) {
        vertNeighbors[a].add(b); vertNeighbors[a].add(c)
        vertNeighbors[b].add(a); vertNeighbors[b].add(c)
        vertNeighbors[c].add(a); vertNeighbors[c].add(b)
      }
      const vertSlope = new Float32Array(verts.length)
      for (let i = 0; i < verts.length; i++) {
        const hi = vertHeight[i]
        const vi = verts[i]
        let maxSlope = 0
        for (const j of vertNeighbors[i]) {
          const vj = verts[j]
          const dot = vi[0] * vj[0] + vi[1] * vj[1] + vi[2] * vj[2]
          const angDist = Math.acos(Math.min(1, Math.max(-1, dot)))
          if (angDist > 0) {
            const slope = Math.abs(vertHeight[j] - hi) / angDist
            if (slope > maxSlope) maxSlope = slope
          }
        }
        vertSlope[i] = Math.min(maxSlope, 1.0)
      }

      if (cancelled) return

      // ── Phase 11: Coastal marking ──────────────────────────────────
      progress(40, 'Marking coastlines...')
      await yieldToMain()
      if (cancelled) return

      const isCoastal = new Uint8Array(verts.length)
      for (let i = 0; i < verts.length; i++) {
        if (vertHeight[i] < HEIGHT_THRESHOLD) continue
        for (const j of vertNeighbors[i]) {
          if (vertHeight[j] < HEIGHT_THRESHOLD) { isCoastal[i] = 1; break }
        }
      }
      const isCoastal2 = new Uint8Array(isCoastal)
      for (let i = 0; i < verts.length; i++) {
        if (vertHeight[i] < HEIGHT_THRESHOLD || isCoastal[i]) continue
        for (const j of vertNeighbors[i]) {
          if (isCoastal[j]) { isCoastal2[i] = 1; break }
        }
      }

      if (cancelled) return

      // ── Phase 12: Build final geometry (chunked) ───────────────────
      progress(41, 'Building vertex colors...')
      await yieldToMain()
      if (cancelled) return

      const totalTris = tris.length
      const positions = new Float32Array(totalTris * 9)
      const colors = new Float32Array(totalTris * 9)

      const CHUNK_SIZE = 80000
      for (let chunkStart = 0; chunkStart < totalTris; chunkStart += CHUNK_SIZE) {
        const chunkEnd = Math.min(chunkStart + CHUNK_SIZE, totalTris)
        const pct = Math.round(41 + (chunkEnd / totalTris) * 4) // 41% → 45%
        progress(pct, `Building vertex colors... ${Math.round(chunkEnd / totalTris * 100)}%`)

        if (chunkStart > 0) {
          await yieldToMain()
          if (cancelled) return
        }

        for (let ti = chunkStart; ti < chunkEnd; ti++) {
          for (let v = 0; v < 3; v++) {
            const vi = tris[ti][v]
            const [nx, ny, nz] = verts[vi]
            const h = vertHeight[vi]
            const r = RADIUS * (1.0 + h)
            const idx = ti * 9 + v * 3

            positions[idx] = nx * r
            positions[idx + 1] = ny * r
            positions[idx + 2] = nz * r

            const cv = noise3(nx * 18 + 777, ny * 18 + 777, nz * 18 + 777) * 0.04
            const bnd = noise3(nx * 10 + 333, ny * 10 + 333, nz * 10 + 333) * 0.008
            const slope = smoothstep(0.04, 0.15, vertSlope[vi])
            const scatter = noise3(nx * 35 + 555, ny * 35 + 555, nz * 35 + 555)
            const isSand = isCoastal2[vi] === 1

            let cr: number, cg: number, cb: number

            if (h < HEIGHT_THRESHOLD) {
              cr = 0.50 + cv; cg = 0.42 + cv * 0.5; cb = 0.30
            } else if (isSand) {
              cr = 0.82 + cv; cg = 0.74 + cv * 0.5; cb = 0.52
            } else if (h < 0.020 + bnd) {
              cr = 0.22 + cv; cg = 0.50 + cv; cb = 0.10 + cv * 0.3
              if (slope > 0.3 && scatter > 0.2) {
                const rockAmt = (slope - 0.3) / 0.7 * smoothstep(0.2, 0.5, scatter)
                cr = lerp(cr, 0.46 + cv, rockAmt)
                cg = lerp(cg, 0.42 + cv * 0.5, rockAmt)
                cb = lerp(cb, 0.36, rockAmt)
              }
            } else if (h < 0.025 + bnd) {
              const blend = smoothstep(0.020 + bnd, 0.025 + bnd, h)
              const rockPatch = scatter > lerp(0.4, -0.2, blend) ? 1.0 : 0.0
              const gr = 0.22 + cv, gg = 0.50 + cv, gb = 0.10 + cv * 0.3
              const rr = 0.46 + cv, rg = 0.42 + cv * 0.5, rb = 0.36
              const t2 = Math.max(blend, rockPatch * 0.8, slope * 0.7)
              cr = lerp(gr, rr, t2); cg = lerp(gg, rg, t2); cb = lerp(gb, rb, t2)
            } else if (h < 0.045 + bnd) {
              cr = 0.46 + cv; cg = 0.42 + cv * 0.5; cb = 0.36
              if (slope < 0.15 && scatter < -0.3 && h < 0.035) {
                const grassAmt = (1 - slope / 0.15) * smoothstep(-0.5, -0.3, -scatter) * 0.6
                cr = lerp(cr, 0.22 + cv, grassAmt)
                cg = lerp(cg, 0.50 + cv, grassAmt)
                cb = lerp(cb, 0.10, grassAmt)
              }
            } else if (h < 0.055 + bnd) {
              const blend = smoothstep(0.045 + bnd, 0.055 + bnd, h)
              const snowPatch = scatter > lerp(0.5, -0.3, blend) ? 1.0 : 0.0
              const t2 = Math.max(blend * 0.7, snowPatch * 0.9) * (1 - slope * 0.5)
              cr = lerp(0.46 + cv, 0.92, t2)
              cg = lerp(0.42 + cv * 0.5, 0.93, t2)
              cb = lerp(0.36, 0.97, t2)
            } else {
              const rockThrough = slope * 0.6
              cr = lerp(0.92, 0.50 + cv, rockThrough)
              cg = lerp(0.93, 0.46 + cv * 0.3, rockThrough)
              cb = lerp(0.97, 0.42, rockThrough)
            }

            colors[idx] = cr; colors[idx + 1] = cg; colors[idx + 2] = cb
          }
        }
      }

      if (cancelled) return

      // ── Finalize ───────────────────────────────────────────────────
      const geo = new THREE.BufferGeometry()
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      geo.setAttribute('color', new THREE.BufferAttribute(colors, 3))
      geo.computeVertexNormals()

      const mat = new THREE.MeshStandardMaterial({
        vertexColors: true,
        roughness: 1.0,
        metalness: 0.0,
        flatShading: false,
      })

      if (!cancelled) {
        // Cache the generated geometry for next load
        const normalsAttr = geo.getAttribute('normal') as THREE.BufferAttribute
        saveCachedTerrain(positions, colors, normalsAttr.array as Float32Array)

        setResult({ geometry: geo, material: mat })
        onReadyRef.current?.()
      }
    }

    generate()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps — run once on mount

  if (!result) return null
  return <mesh ref={meshRef} geometry={result.geometry} material={result.material} />
}
